import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import Box from "@mui/material/Box";
import {
  clampDetailsWorkflowPaneWidth,
  DETAILS_PANE_DEFAULT_RATIO,
  DETAILS_WORKFLOW_SPLITTER_PX,
  WORKFLOW_PANE_MAX_VIEWPORT_RATIO,
  WORKFLOW_PANE_MIN_PX,
} from "../../utils/wholeWorkflowIframe";
import {
  DETAILS_SPLIT_PANE_SX,
  WholeWorkflowSidePane,
} from "./WholeWorkflowSidePane";

const SPLIT_NUDGE_PX = 24;

const DETAILS_PANE_BASE_SX = {
  display: "flex",
  flexDirection: "column" as const,
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  position: "relative" as const,
};

export interface DetailsWorkflowSplitLayoutProps {
  workflowUri: string | null;
  onCloseWorkflow: () => void;
  children: ReactNode;
  backToTop?: ReactNode;
}

export function DetailsWorkflowSplitLayout({
  workflowUri,
  onCloseWorkflow,
  children,
  backToTop,
}: DetailsWorkflowSplitLayoutProps) {
  const split = Boolean(workflowUri);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [detailsWidthPx, setDetailsWidthPx] = useState<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);

  const applyDetailsWidth = useCallback((desiredPx: number) => {
    const containerWidth = rowRef.current?.getBoundingClientRect().width ?? 0;
    setDetailsWidthPx(
      clampDetailsWorkflowPaneWidth(
        desiredPx,
        containerWidth,
        window.innerWidth,
      ),
    );
  }, []);

  useEffect(() => {
    if (!split) {
      setIsResizing(false);
      return;
    }

    const row = rowRef.current;
    if (!row) return;

    const syncFromContainer = () => {
      const containerWidth = row.getBoundingClientRect().width;
      setDetailsWidthPx((current) =>
        clampDetailsWorkflowPaneWidth(
          current ?? containerWidth * DETAILS_PANE_DEFAULT_RATIO,
          containerWidth,
          window.innerWidth,
        ),
      );
    };

    syncFromContainer();
    const observer = new ResizeObserver(syncFromContainer);
    observer.observe(row);
    return () => observer.disconnect();
  }, [split]);

  const handleSplitterMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      const row = rowRef.current;
      if (!row) return;

      setIsResizing(true);
      const rowLeft = row.getBoundingClientRect().left;

      const handleMove = (moveEvent: MouseEvent) => {
        applyDetailsWidth(moveEvent.clientX - rowLeft);
      };
      const handleUp = () => {
        setIsResizing(false);
        window.removeEventListener("mousemove", handleMove);
        window.removeEventListener("mouseup", handleUp);
      };

      window.addEventListener("mousemove", handleMove);
      window.addEventListener("mouseup", handleUp);
    },
    [applyDetailsWidth],
  );

  const handleSplitterKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const current =
        detailsWidthPx ??
        (rowRef.current?.getBoundingClientRect().width ?? 0) *
          DETAILS_PANE_DEFAULT_RATIO;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        applyDetailsWidth(current - SPLIT_NUDGE_PX);
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        applyDetailsWidth(current + SPLIT_NUDGE_PX);
        return;
      }
      if (event.key === "Home") {
        event.preventDefault();
        applyDetailsWidth(0);
        return;
      }
      if (event.key === "End") {
        event.preventDefault();
        applyDetailsWidth(Number.POSITIVE_INFINITY);
      }
    },
    [applyDetailsWidth, detailsWidthPx],
  );

  const detailsPane = (
    <Box
      data-testid="regulation-details-pane"
      sx={
        split
          ? {
              ...DETAILS_SPLIT_PANE_SX,
              position: "relative",
              flex: "none",
              width: detailsWidthPx ?? `${DETAILS_PANE_DEFAULT_RATIO * 100}%`,
              minWidth: 0,
            }
          : DETAILS_PANE_BASE_SX
      }
    >
      {children}
      {backToTop ? (
        <Box
          data-testid="details-back-to-top-anchor"
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 1000,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              right: 32,
              bottom: 32,
              pointerEvents: "auto",
            }}
          >
            {backToTop}
          </Box>
        </Box>
      ) : null}
    </Box>
  );

  if (!split || !workflowUri) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        {detailsPane}
      </Box>
    );
  }

  return (
    <Box
      ref={rowRef}
      data-testid="details-workflow-split-row"
      data-resizing={isResizing ? "true" : "false"}
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        alignItems: "stretch",
        px: 1.5,
        pb: 1.5,
        userSelect: isResizing ? "none" : "auto",
        "& iframe": {
          pointerEvents: isResizing ? "none" : "auto",
        },
      }}
    >
      {detailsPane}
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize View whole workflow"
        aria-valuenow={detailsWidthPx ?? undefined}
        tabIndex={0}
        data-testid="details-workflow-splitter"
        onMouseDown={handleSplitterMouseDown}
        onKeyDown={handleSplitterKeyDown}
        sx={{
          width: DETAILS_WORKFLOW_SPLITTER_PX,
          flexShrink: 0,
          cursor: "col-resize",
          alignSelf: "stretch",
          bgcolor: "transparent",
          "&:hover, &:focus-visible": {
            bgcolor: "rgba(41, 94, 241, 0.12)",
          },
        }}
      />
      <WholeWorkflowSidePane
        uri={workflowUri}
        onClose={onCloseWorkflow}
        sx={{
          minWidth: WORKFLOW_PANE_MIN_PX,
          maxWidth: `${WORKFLOW_PANE_MAX_VIEWPORT_RATIO * 100}vw`,
        }}
      />
    </Box>
  );
}
